const getActiveStep = (form) => {
  return form.querySelector('[data-accordion].accordion--active')
}

const openStep = (form, targetStep) => {
  const activeStep = getActiveStep(form)
  const activeIndex = activeStep?.dataset.accordion

  if (activeIndex && typeof form.closeAccordion === 'function') {
    form.closeAccordion(parseInt(activeIndex, 10))
  }

  if (typeof form.openAccordion === 'function') {
    form.openAccordion(targetStep)
  }
}

const getFormTrigger = (form) => {
  if (!form.id) return null
  return Array.from(document.querySelectorAll('[data-fm-open-form]')).find(
    trigger => trigger.getAttribute('aria-controls') === form.id
  )
}

const openConfigurator = (form, trigger) => {
  form.hidden = false
  form.dataset.fmOpen = 'true'
  document.body.classList.add('fm-form-redesign-open')

  if (trigger) {
    trigger.setAttribute('aria-expanded', 'true')
  }

  const closeButton = form.querySelector('[data-fm-close]')
  if (closeButton) {
    closeButton.focus()
  }
}

const closeConfigurator = (form, trigger) => {
  closeExitConfirm(form, false)
  form.hidden = true
  form.dataset.fmOpen = 'false'
  document.body.classList.remove('fm-form-redesign-open')

  if (trigger) {
    trigger.setAttribute('aria-expanded', 'false')
    trigger.focus()
  }
}

const getExitConfirm = (form) => {
  return form.querySelector('[data-fm-exit-confirm]')
}

const getExitConfirmButtons = (form) => {
  return Array.from(form.querySelectorAll('[data-fm-exit-stay], [data-fm-exit-exit]'))
}

const openExitConfirm = (form, returnFocusElement) => {
  const confirm = getExitConfirm(form)
  if (!confirm) return false

  form._fmExitReturnFocus = returnFocusElement
  confirm.hidden = false
  form.dataset.fmExitConfirmOpen = 'true'

  const stayButton = form.querySelector('[data-fm-exit-stay]')
  if (stayButton) stayButton.focus()

  return true
}

const closeExitConfirm = (form, restoreFocus = true) => {
  const confirm = getExitConfirm(form)
  if (!confirm) return

  confirm.hidden = true
  form.dataset.fmExitConfirmOpen = 'false'

  const returnFocusElement = form._fmExitReturnFocus
  form._fmExitReturnFocus = null

  if (restoreFocus && returnFocusElement?.isConnected) {
    returnFocusElement.focus()
  }
}

const bindExitConfirm = (form, trigger) => {
  form.querySelectorAll('[data-fm-close]').forEach(button => {
    button.addEventListener('click', () => {
      if (!openExitConfirm(form, button)) {
        closeConfigurator(form, trigger)
      }
    })
  })

  form.querySelector('[data-fm-exit-stay]')?.addEventListener('click', () => {
    closeExitConfirm(form)
  })

  form.querySelector('[data-fm-exit-exit]')?.addEventListener('click', () => {
    closeExitConfirm(form, false)
    closeConfigurator(form, trigger)
  })

  getExitConfirm(form)?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeExitConfirm(form)
      return
    }

    if (event.key !== 'Tab') return

    const buttons = getExitConfirmButtons(form)
    if (buttons.length === 0) return

    const firstButton = buttons[0]
    const lastButton = buttons[buttons.length - 1]

    if (event.shiftKey && document.activeElement === firstButton) {
      event.preventDefault()
      lastButton.focus()
    } else if (!event.shiftKey && document.activeElement === lastButton) {
      event.preventDefault()
      firstButton.focus()
    }
  })
}

const bindFullscreenLauncher = (form) => {
  const trigger = getFormTrigger(form)

  if (trigger) {
    trigger.addEventListener('click', () => {
      openConfigurator(form, trigger)
    })
  }

  bindExitConfirm(form, trigger)
}

const bindEstimateCards = (form) => {
  const estimateCards = form.querySelectorAll('[data-fm-size-card]')

  estimateCards.forEach(card => {
    card.addEventListener('click', () => {
      estimateCards.forEach(otherCard => {
        otherCard.classList.remove('fm-estimate-card--selected')
        otherCard.setAttribute('aria-pressed', 'false')
      })

      card.classList.add('fm-estimate-card--selected')
      card.setAttribute('aria-pressed', 'true')
      openStep(form, 2)
    })
  })
}

const getFaqTogglesForDrawer = (form, drawer) => {
  if (!drawer?.id) return []

  return Array.from(form.querySelectorAll('[data-fm-faq-toggle]'))
    .filter(toggle => toggle.getAttribute('aria-controls') === drawer.id)
}

const closeFaqDrawer = (form, drawer) => {
  if (!drawer) return

  drawer.hidden = true
  getFaqTogglesForDrawer(form, drawer).forEach(toggle => {
    toggle.setAttribute('aria-expanded', 'false')
    if (toggle.classList.contains('fm-faq-bar')) {
      toggle.hidden = false
    }
  })
}

const openFaqDrawer = (form, drawer) => {
  if (!drawer) return

  form.querySelectorAll('[data-fm-faq-drawer]').forEach(otherDrawer => {
    if (otherDrawer !== drawer) closeFaqDrawer(form, otherDrawer)
  })

  drawer.hidden = false
  getFaqTogglesForDrawer(form, drawer).forEach(relatedToggle => {
    relatedToggle.setAttribute('aria-expanded', 'true')
    if (relatedToggle.classList.contains('fm-faq-bar')) {
      relatedToggle.hidden = true
    }
  })
}

const getFaqDrawerForToggle = (form, toggle) => {
  const controls = toggle.getAttribute('aria-controls')
  if (controls) {
    const controlledDrawer = document.getElementById(controls)
    if (controlledDrawer && form.contains(controlledDrawer)) return controlledDrawer
  }

  return getActiveStep(form)?.querySelector('[data-fm-faq-drawer]')
}

const bindFaqDrawers = (form) => {
  form.querySelectorAll('[data-fm-faq-toggle]').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const drawer = getFaqDrawerForToggle(form, toggle)
      const isOpen = drawer && !drawer.hidden

      if (isOpen) {
        closeFaqDrawer(form, drawer)
      } else {
        openFaqDrawer(form, drawer)
      }
    })
  })

  form.querySelectorAll('[data-fm-faq-close]').forEach(button => {
    button.addEventListener('click', () => {
      const drawer = button.closest('[data-fm-faq-drawer]')
      closeFaqDrawer(form, drawer)
      const toggle = getFaqTogglesForDrawer(form, drawer)[0]

      if (toggle) {
        toggle.focus()
      }
    })
  })
}

const openGorgiasChat = () => {
  const openChat = () => {
    if (typeof window.GorgiasChat?.open !== 'function') return false

    window.GorgiasChat.open()
    return true
  }

  if (openChat()) return

  if (typeof window.GorgiasChat?.init === 'function') {
    const initResult = window.GorgiasChat.init()
    if (typeof initResult?.then === 'function') {
      initResult.then(openChat).catch(() => {})
    } else {
      openChat()
    }
    return
  }

  window.addEventListener('gorgias-widget-loaded', openChat, { once: true })
}

const bindGorgiasHelpButtons = (form) => {
  form.querySelectorAll('[data-fm-gorgias-help]').forEach(button => {
    button.addEventListener('click', (event) => {
      event.preventDefault()
      openGorgiasChat()
    })
  })
}

const syncStyleCards = (form) => {
  const styleCards = form.querySelectorAll('[data-fm-style-card]')

  styleCards.forEach(card => {
    const input = card.querySelector('[data-mirror-type-checkbox]')
    const isSelected = Boolean(input?.checked)
    card.classList.toggle('fm-style-card--selected', isSelected)
    card.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
  })
}

const updateHandlingFeeProduct = (form) => {
  const selectedMirrorType = form.querySelector('input[data-mirror-type-checkbox]:checked')
  const handlingFeeInput = form.querySelector('[handling-fee-form] input[name="id"]')
  const handlingFeeInputId = form.querySelector('[data-handling-fee-id]')
  const handlingFeeText = form.querySelector('[data-handling-fee-text]')
  const handlingFeeBeveledText = form.querySelector('[data-handling-fee-beveled-text]')
  const handlingFeeAntiquedText = form.querySelector('[data-handling-fee-antiqued-text]')

  if (!selectedMirrorType || !handlingFeeInput || !handlingFeeInputId) return

  const standardFeeId = form.dataset.fmFeeStandard
  const beveledFeeId = form.dataset.fmFeeBeveled || standardFeeId
  const antiquedFeeId = form.dataset.fmFeeAntiqued || standardFeeId
  let nextFeeId = standardFeeId
  let visibleText = handlingFeeText

  if (selectedMirrorType.value === 'Beveled' && beveledFeeId) {
    nextFeeId = beveledFeeId
    visibleText = handlingFeeBeveledText
  } else if (selectedMirrorType.value === 'Antiqued' && antiquedFeeId) {
    nextFeeId = antiquedFeeId
    visibleText = handlingFeeAntiquedText
  }

  if (nextFeeId) {
    handlingFeeInput.value = nextFeeId
    handlingFeeInputId.value = nextFeeId
  }

  ;[handlingFeeText, handlingFeeBeveledText, handlingFeeAntiquedText].forEach(text => {
    if (!text) return
    text.style.display = text === visibleText ? 'inline-block' : 'none'
  })
}

const bindStyleCards = (form) => {
  form.querySelectorAll('[data-mirror-type-checkbox]').forEach(input => {
    input.addEventListener('change', () => {
      syncStyleCards(form)
      updateHandlingFeeProduct(form)
    })
  })

  syncStyleCards(form)
}

const bindReviewEditLinks = (form) => {
  form.querySelectorAll('[data-fm-edit-section]').forEach(button => {
    button.addEventListener('click', () => {
      const targetStep = parseInt(button.dataset.fmEditSection, 10)
      if (!Number.isNaN(targetStep)) {
        openStep(form, targetStep)
      }
    })
  })
}

const bindReviewToggle = (form) => {
  form.querySelectorAll('[data-fm-review-toggle]').forEach(button => {
    button.addEventListener('click', () => {
      const reviewCard = button.closest('[data-fm-review-card]')
      if (!reviewCard) return

      const isCollapsed = reviewCard.dataset.fmCollapsed === 'true'
      reviewCard.dataset.fmCollapsed = isCollapsed ? 'false' : 'true'
      button.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false')
      button.textContent = isCollapsed ? '-' : '+'
    })
  })
}

const initFramedMirrorRedesign = () => {
  document.querySelectorAll('frame-my-mirror-form[data-fm-form]').forEach(form => {
    bindFullscreenLauncher(form)
    bindEstimateCards(form)
    bindFaqDrawers(form)
    bindGorgiasHelpButtons(form)
    bindStyleCards(form)
    bindReviewEditLinks(form)
    bindReviewToggle(form)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFramedMirrorRedesign)
} else {
  initFramedMirrorRedesign()
}
